import React, { useRef, useState } from 'react';
import { Button, Col, Row, Icon, Tooltip, Modal, Badge, Input } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import RulesContainer from '../../../../shared/containers/RulesContainer';
import HelpTooltipIcon, { HelpIcon } from '../../../../shared/components/HelpTooltipIcon';
import ScreensOutputSettingsHolder from '../../styles/screensOutputSettingsStyle';
import logoAI from '../../../../../images/ICONO-ESTRELLAS-IA-blanco.png';
import { getDomain, isCpmiCompany } from '../../../../../services/utils';
import { getRulesEditableContents } from '../../services/rulesPermissions';
import OptimizeRulesWizardComponent from './OptimizeRulesWizardComponent';
import Colors from '../../../../../themes/Colors';
import { getPlayoutsLabel, getPlayoutsQualifier } from './supporting/PlayoutsLabel';

const { Search } = Input;

const ScreensOutputSettingsComponent = props => {
  const domain = getDomain(window.location.hostname);
  const { t } = useTranslation();

  const [inputSearchValue, setInputSearchValue] = useState('');
  const [rulesModalVisible, setRulesModalVisible] = useState(false);
  const [isModalVisibleAssignSpots, setIsModalVisibleAssignSpots] = useState(false);
  const [isModalVisibleBalance, setIsModalVisibleBalance] = useState(false);
  const [optimizeWizardVisible, setOptimizeWizardVisible] = useState(false);
  const searchInputRef = useRef(null);

  const {
    customContents,
    cart,
    realAmount,
    updateCustomContents,
    isPaymentStatusApproved,
    programmatic,
    isGuaranteed,
    date_from,
    date_to,
    audience,
    updateRulesRelationScreen,
    setLibraryRules,
    optimizeRulesRequest,
    loadingOptimizeRules,
    optimizeRulesResult,
    optimizeRulesError,
    user,
  } = props;

  // cpmi companies buy 100% by impressions: no playouts assign/balance.
  const isCpmi = isCpmiCompany(user);

  const playoutsQualifier = getPlayoutsQualifier({ programmatic, isGuaranteed }, t);

  const rulesContents = getRulesEditableContents({ customContents, cart, user, programmatic });

  const calculateRules = () => {
    if (!rulesContents.length || !Array.isArray(rulesContents)) return 0;
    const uniqueIds = new Set();
    rulesContents.forEach(content => {
      if (content.rules && Array.isArray(content.rules)) {
        content.rules.forEach(rule => {
          if (rule.configRules && rule.configRules.id) {
            uniqueIds.add(rule.configRules.id);
          }
        });
      }
    });
    return uniqueIds.size;
  };

  const hasNoAudienceData = () => {
    if (!audience || Object.keys(audience).length === 0) return false;
    return !audience.total_audience;
  };

  const isOptimizeDisabled = !customContents.length || hasNoAudienceData();

  const handleApplyOptimizedRules = (rulesByDisplay, allVersions) => {
    const seenIds = new Set();
    const allLibraryRules = [];

    customContents.forEach(cc => {
      const rules = rulesByDisplay[String(cc.displayId)];
      if (!rules) return;

      const enrichedRules = rules.map(rule => ({
        ...rule,
        configRules: {
          ...rule.configRules,
          content_version_name: allVersions,
          original_version_selected: allVersions,
        },
      }));

      updateRulesRelationScreen(cc.displayId, cc.relationId, enrichedRules);

      enrichedRules.forEach(rule => {
        const id = rule.configRules && rule.configRules.id;
        if (id !== undefined && !seenIds.has(id)) {
          seenIds.add(id);
          allLibraryRules.push(rule);
        }
      });
    });

    setLibraryRules(allLibraryRules);
  };

  const handleInputSearchChange = e => {
    let value = e.target.value;
    value = value.replace(/^0+/, '').replace(/[^0-9]/g, '');
    if (Number(value) > 0) {
      setInputSearchValue(value);
    } else if (value === '') {
      setInputSearchValue('');
    }
  };

  const handleInputSearch = () => {};

  const handleBalanceModal = () => setIsModalVisibleBalance(v => !v);

  const handleBalanceOk = () => {
    redistributeImpressionsEquitably();
    setIsModalVisibleBalance(false);
  };

  const redistributeImpressionsEquitably = () => {
    const listCostDisplay = cart.reduce((acc, display) => {
      acc[display.id] = display.smart_campaign_cpm / 1000;
      return acc;
    }, {});
    const totalCostPerImpression = customContents.reduce(
      (acc, cc) => acc + (listCostDisplay[cc.displayId] || 0),
      0
    );
    const totalImpressions = Math.floor(realAmount / totalCostPerImpression);
    let assignedBudget = 0;
    let customContentsAux = customContents.map(cc => {
      assignedBudget += totalImpressions;
      return { ...cc, promised_shows: totalImpressions };
    });
    let remainingBudget = realAmount - assignedBudget;
    for (let i = 0; i < customContentsAux.length && remainingBudget > 0; i++) {
      const extraCost = customContentsAux[i].smart_campaign_cpm;
      if (remainingBudget >= extraCost) {
        customContentsAux[i].promised_shows += 1;
        remainingBudget -= extraCost;
      }
    }
    updateCustomContents(customContentsAux);
  };

  const showModalAssignSpots = () => {
    if (!inputSearchValue) {
      if (searchInputRef.current) searchInputRef.current.focus();
      return;
    }
    setIsModalVisibleAssignSpots(true);
  };

  const handleOkAssignSpots = () => {
    assignSpotsToAllScreens();
    setIsModalVisibleAssignSpots(false);
  };

  const assignSpotsToAllScreens = () => {
    const spots = Number(inputSearchValue);
    if (isNaN(spots) || spots <= 0) return;
    updateCustomContents(customContents.map(cc => ({ ...cc, promised_shows: spots })));
  };

  return (
    <ScreensOutputSettingsHolder>
      <p className="stepSectionTitle">
        {programmatic === 'programmatic' ? t('Frequency settings') : t('Output settings')}
      </p>

      <Row type="flex" align="middle" justify="center">
        <Col xs={24}>
          <div className="rulesActionsRow">
            <div className="createRulesWrapper">
              <Badge count={calculateRules()} style={{ backgroundColor: Colors.primary }}>
                <Tooltip placement="bottomLeft" title={t('Set up rules in campaign')}>
                  <Button
                    onClick={() => setRulesModalVisible(true)}
                    className="createRulesBtn"
                    size="default"
                  >
                    {t('Create rules')}
                    <Icon type="control" />
                  </Button>
                </Tooltip>
              </Badge>
            </div>

            {programmatic === 'programmatic' && (
              <Tooltip
                placement="bottomLeft"
                title={
                  isOptimizeDisabled
                    ? t('No audience data available to optimize')
                    : t('Optimize rules')
                }
              >
                <Button
                  className={`searchAIPlannerButton ${domain === 'dv360' ? 'dv360' : ''}`}
                  type="primary"
                  disabled={isOptimizeDisabled}
                  onClick={() => setOptimizeWizardVisible(true)}
                >
                  {t('Optimize')}
                  <Icon type="control" />
                  <img src={logoAI} style={{ maxHeight: '30px' }} alt="" />
                </Button>
              </Tooltip>
            )}
          </div>

          <div className="contentGroupFilter">
            {programmatic === 'programmatic' && !isPaymentStatusApproved && !isCpmi && (
              <div className="frequencyRow">
                <Search
                  ref={searchInputRef}
                  className="btnAssignSpots"
                  placeholder={getPlayoutsLabel({ programmatic, isGuaranteed }, t)}
                  enterButton={
                    <Tooltip placement="top" title={t('Assign playouts tooltip', { qualifier: playoutsQualifier })}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                        onClick={showModalAssignSpots}
                      >
                        <span style={{ color: Colors.white }}>{t('Assign')}</span>
                      </div>
                    </Tooltip>
                  }
                  size="default"
                  value={inputSearchValue}
                  min={1}
                  type="number"
                  onChange={handleInputSearchChange}
                  onSearch={handleInputSearch}
                />

                <Tooltip placement="top" title={t('Balance playouts tooltip', { qualifier: playoutsQualifier })}>
                  <Button
                    onClick={handleBalanceModal}
                    className="balanceBtn"
                    size="default"
                  >
                    {t('Balance')}
                  </Button>
                </Tooltip>

                <Modal
                  title={t('Confirm balance playouts title')}
                  visible={isModalVisibleBalance}
                  onOk={handleBalanceOk}
                  onCancel={() => setIsModalVisibleBalance(false)}
                  okText={t('Balance')}
                  cancelText={t('Cancel')}
                >
                  <p style={{ margin: '20px', fontSize: '16px' }}>
                    {t('Confirm balance playouts body', { qualifier: playoutsQualifier })}
                  </p>
                </Modal>

                <Modal
                  title={t('Confirm assign playouts title')}
                  visible={isModalVisibleAssignSpots}
                  onOk={handleOkAssignSpots}
                  onCancel={() => setIsModalVisibleAssignSpots(false)}
                  okText={t('Assign')}
                  cancelText={t('Cancel')}
                >
                  <p style={{ margin: '20px', fontSize: '16px' }}>
                    {t('Confirm assign playouts body', { qualifier: playoutsQualifier })}
                  </p>
                </Modal>
              </div>
            )}

            <Modal
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {t('Rules in campaign')}
                  <HelpTooltipIcon
                    href="https://www.youtube.com/watch?v=VuoK7wMlcJc"
                    tooltip={
                      <Trans
                        i18nKey="Rules tooltip"
                        components={{ icon: <HelpIcon /> }}
                      />
                    }
                  />
                </div>
              }
              visible={rulesModalVisible}
              footer={[
                <Button key="ok" type="primary" onClick={() => setRulesModalVisible(false)}>
                  {t('Close')}
                </Button>,
              ]}
              onCancel={() => setRulesModalVisible(false)}
              width={800}
            >
              <RulesContainer typeModal="campaign" listContentDisplay={rulesContents} />
            </Modal>
          </div>
        </Col>
      </Row>

      <OptimizeRulesWizardComponent
        visible={optimizeWizardVisible}
        onClose={() => setOptimizeWizardVisible(false)}
        onApply={handleApplyOptimizedRules}
        customContents={customContents}
        dateFrom={date_from}
        dateTo={date_to}
        onOptimizeRequest={optimizeRulesRequest}
        loading={loadingOptimizeRules}
        result={optimizeRulesResult}
        error={optimizeRulesError}
      />
    </ScreensOutputSettingsHolder>
  );
};

export default ScreensOutputSettingsComponent;
